import { useIsMobile } from "../hooks/useIsMobile";
import { usePostActions } from "../hooks/usePostActions";
import { isPostEditable } from "../utils/postStatus";
import { Play } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { loadMediaUrls } from "../lib/utils";
import { PlatformConnection } from "../models/Platform";
import { Post, isVideoPost } from "../models/Post";
import { MediaPlayer } from "./MediaPlayer";
import { PostViewer } from "./PostViewer";

export const MediaGallery = React.memo(function MediaGallery({
  expandedPost,
  connectionsById,
  mediaUrls: externalMediaUrls,
}: {
  expandedPost: Post;
  connectionsById: Record<string, PlatformConnection>;
  mediaUrls?: Record<string, string>;
}) {
  const isMobile = useIsMobile();
  const { handleEdit } = usePostActions();
  const currentIndex = 0;
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [internalMediaUrls, setInternalMediaUrls] = useState<Record<string, string>>({});

  // Use external URLs if provided, otherwise use internal state
  const activeMediaUrls = externalMediaUrls || internalMediaUrls;

  // Create a dependency that will change when thumbnail paths change
  const mediaItemsSignature = useMemo(() => {
    return expandedPost.mediaItems
      .map((item) => `${item.path}:${item.metadata?.thumbnailPath || "none"}`)
      .join("|");
  }, [expandedPost.mediaItems]);

  // Only load URLs if they weren't provided externally
  useEffect(() => {
    if (externalMediaUrls) {
      return; // Skip loading if URLs are provided from parent
    }

    const loadUrls = async () => {
      try {
        const urlEntries = await loadMediaUrls([expandedPost]);
        setInternalMediaUrls(urlEntries);
      } catch (error) {
        console.error(`[MediaGallery] Error loading URLs:`, error);
      }
    };

    loadUrls();
  }, [expandedPost, mediaItemsSignature, externalMediaUrls]); // Add externalMediaUrls as dependency

  if (expandedPost.mediaItems.length === 0) {
    return null;
  }

  const isVideoMode = isVideoPost(expandedPost);

  return (
    <>
      <div
        className="relative isolate w-full h-full cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          if (isMobile && isPostEditable(expandedPost)) {
            handleEdit(expandedPost.id);
          } else {
            setIsViewerOpen(true);
          }
        }}
      >
        {isVideoMode ? (
          <>
            {/* Video mode: show first slide as thumbnail with play button */}
            {expandedPost.mediaItems[0] && (
              <div className="absolute w-full h-full">
                <MediaPlayer
                  mediaItem={expandedPost.mediaItems[0]}
                  isControlsVisible={false}
                  autoPlay={false}
                  loop={false}
                  muted={true}
                  mediaUrls={activeMediaUrls}
                  lazyLoadOverlays={true}
                />
              </div>
            )}
            {/* Play button overlay */}
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="bg-black/50 backdrop-blur-sm rounded-full p-4 group-hover:bg-black/70 transition-colors">
                <Play className="h-8 w-8 text-white fill-white" />
              </div>
            </div>
          </>
        ) : (
          <>
            {expandedPost.mediaItems.length > 1 && (
              <div className="absolute bottom-2 right-2 z-10 bg-black/50 rounded-full px-2 py-1 text-white text-xs">
                {currentIndex + 1} / {expandedPost.mediaItems.length}
              </div>
            )}
            {/* Only the cover is visible here. Mounting the other slides also
                creates their media and overlay players, even at opacity zero. */}
            <div
              key={`${expandedPost.mediaItems[currentIndex].path}-${expandedPost.mediaItems[currentIndex].metadata?.thumbnailPath || "none"}`}
              className="absolute w-full h-full"
            >
              <MediaPlayer
                mediaItem={expandedPost.mediaItems[currentIndex]}
                isControlsVisible={false}
                autoPlay={false}
                loop={false}
                muted={true}
                mediaUrls={activeMediaUrls}
                lazyLoadOverlays={true}
              />
            </div>
          </>
        )}
      </div>
      {/* Detailed view */}
      {isViewerOpen && (
        <PostViewer
          isOpen={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
          expandedPost={expandedPost}
          initialIndex={currentIndex}
          connectionsById={connectionsById}
          mediaUrls={activeMediaUrls}
        />
      )}
    </>
  );
}, (prevProps, nextProps) => {
  if (prevProps.expandedPost.id !== nextProps.expandedPost.id) return false;
  if (prevProps.expandedPost.updatedAt !== nextProps.expandedPost.updatedAt) return false;
  if (
    isVideoPost(prevProps.expandedPost) !== isVideoPost(nextProps.expandedPost)
  ) return false;
  if (
    prevProps.expandedPost.videoChoreographyHash !==
    nextProps.expandedPost.videoChoreographyHash
  ) return false;
  if (
    prevProps.expandedPost.videoMusicPath !==
    nextProps.expandedPost.videoMusicPath
  ) return false;
  if (
    prevProps.expandedPost.videoAudioMuted !==
    nextProps.expandedPost.videoAudioMuted
  ) return false;
  if (
    prevProps.expandedPost.videoSpeed !== nextProps.expandedPost.videoSpeed
  ) return false;

  const prevUrls = prevProps.mediaUrls;
  const nextUrls = nextProps.mediaUrls;

  if (!prevUrls && !nextUrls) return true;
  if (!prevUrls || !nextUrls) return false;

  for (const mediaItem of nextProps.expandedPost.mediaItems) {
    if (!mediaItem) continue;
    if (prevUrls[mediaItem.path] !== nextUrls[mediaItem.path]) return false;
    if (mediaItem.type === "video" && mediaItem.metadata?.thumbnailPath) {
      if (prevUrls[mediaItem.metadata.thumbnailPath] !== nextUrls[mediaItem.metadata.thumbnailPath]) {
        return false;
      }
    }
    // Also check overlay image URLs
    if (mediaItem.metadata?.overlays) {
      for (const overlay of mediaItem.metadata.overlays) {
        if (overlay.type === "image" && "filePath" in overlay && overlay.filePath) {
          if (prevUrls[overlay.filePath] !== nextUrls[overlay.filePath]) return false;
        }
      }
    }
  }

  return true;
});

export default MediaGallery;
